import React, { useContext, useEffect, useMemo, useState } from "react";
import "./RentConfirmation.css";
import InputField from "../../components/InputField/InputField";
import { TbCreditCard, TbX } from "react-icons/tb";
import Button from "../../components/Button/Button";
import { useLocation, useNavigate } from "react-router-dom";
import BackButton from "../../components/BackButton/BackButton";
import { MallContext } from "../../MallContext";
import Services from "../../services/Services";
import { useLanguage } from "../../LanguageProvider";
import PhoneInput from "../../components/PhoneInput/PhoneInput";
import SuccessModal from "../../components/SuccessModal/SuccessModal";
import Checkbox from "../../components/Checkbox/Checkbox";
import SaveAddressModal from "../../components/SaveAddressModal/SaveAddressModal";
import AddressSelectBar from "../../components/AddressSelectBar/AddressSelectBar";
import { generateRandomCaseSensitiveString } from "../../utils/generateRandomCaseSensitiveString";
import { getAgreementTexts, getPetAgreementTexts } from "../../utils/agreementTemplates";
import {
  getConfirmedAgreementTexts,
  loginDocuments
} from "../../store/legalDocuments";

function RentConfirmation() {
  const { mallInfo, token, poiID } = useContext(MallContext);
  const { getTranslation } = useLanguage();
  const canUsePhysicalPos = String(mallInfo?.hasPhysicalPOS) === "1";
  const canUseCash = String(mallInfo?.isCash) === "1";
  const location = useLocation();
  const navigate = useNavigate();
  const loginMethod = localStorage.getItem('loginMethod');
  const {
    selectedService,
    dailyPrice,
    packageCount,
    paymentType = "withCreditCard",
    selectedDurationKey,
    type,
    requestID,
    originalWillDeliverDateTime = ""
  } = location.state || {};

  const [userIP, setUserIP] = useState("");
  const [currentPaymentType, setCurrentPaymentType] = useState(paymentType);
  const [effectivePrice, setEffectivePrice] = useState(Number(dailyPrice) || 0);
  const [hasCouponApplied, setHasCouponApplied] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [email, setEmail] = useState("");
  const [tckn, setTckn] = useState("");

  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [district, setDistrict] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("Türkiye");

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("+90");

  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [taxNumber, setTaxNumber] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSaveAddressModalOpen, setIsSaveAddressModalOpen] = useState(false);
  const isCashPayment =
    currentPaymentType === "withCash" || currentPaymentType === "withPhysicalPosCash";
  const isPhysicalPosPayment = currentPaymentType === "withPhysicalPos";
  const isOfflinePayment = isCashPayment || isPhysicalPosPayment;

  useEffect(() => {
    if (isPhysicalPosPayment && !canUsePhysicalPos) {
      setCurrentPaymentType("withCreditCard");
      return;
    }

    if (isCashPayment && !canUseCash) {
      setCurrentPaymentType("withCreditCard");
    }
  }, [canUseCash, canUsePhysicalPos, isCashPayment, isPhysicalPosPayment]);

  const [viewingDocumentHtml, setViewingDocumentHtml] = useState(null);
  const [viewingDocumentTitle, setViewingDocumentTitle] = useState("");

  const [saveAddress, setSaveAddress] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedSavedAddressId, setSelectedSavedAddressId] = useState(null);
  const isUsingSavedAddress = !!selectedSavedAddressId;
  const [addressName, setAddressName] = useState("");

  const [isCancellationPolicyAccepted, setIsCancellationPolicyAccepted] = useState(false);
  const [isKidCombinedAgreementAccepted, setIsKidCombinedAgreementAccepted] = useState(false);
  const [isPetCombinedAgreementAccepted, setIsPetCombinedAgreementAccepted] = useState(false);

  const [showCancellationError, setShowCancellationError] = useState(false);
  const [showAgreementError, setShowAgreementError] = useState(false);

  const [firstName, setFirstName] = useState(localStorage.getItem("name") || "");
  const [lastName, setLastName] = useState(localStorage.getItem("lastname") || "");
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponValidationMessage, setCouponValidationMessage] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [isCouponValidated, setIsCouponValidated] = useState(false);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [isCouponCompleting, setIsCouponCompleting] = useState(false);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [usePhysicalPosCoupon, setUsePhysicalPosCoupon] = useState(false);
  const [physicalPosCouponCode, setPhysicalPosCouponCode] = useState("");
  const [physicalPosCouponData, setPhysicalPosCouponData] = useState(null);
  const [physicalPosDiscountedPrice, setPhysicalPosDiscountedPrice] = useState(null);
  const [isPhysicalPosCouponValidated, setIsPhysicalPosCouponValidated] = useState(false);
  const [isPhysicalPosCouponLoading, setIsPhysicalPosCouponLoading] = useState(false);
  const [physicalPosCouponMessage, setPhysicalPosCouponMessage] = useState("");

  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIP(data.ip);
      } catch (error) {
        console.error("IP Error:", error);
        setUserIP("127.0.0.1");
      }
    };
    fetchIP();
  }, []);

  const baseTotalPrice = useMemo(() => {
    const base = Number(dailyPrice) || 0;
    if (!base) return 0;
    if (selectedService === "gift") {
      return base * (packageCount || 1);
    }
    return base;
  }, [dailyPrice, selectedService, packageCount]);

  useEffect(() => {
    setIsCouponValidated(false);
    setCouponData(null);
    setCouponValidationMessage("");
    setDiscountedPrice(null);
    setHasCouponApplied(false);
    setEffectivePrice(Number(dailyPrice) || 0);
    setCurrentPaymentType(paymentType);
    setIsCouponLoading(false);
    setIsCouponCompleting(false);
    setIsSubmittingRequest(false);
  }, [couponCode, dailyPrice, paymentType]);

  useEffect(() => {
    setIsPhysicalPosCouponValidated(false);
    setPhysicalPosCouponData(null);
    setPhysicalPosDiscountedPrice(null);
    setPhysicalPosCouponMessage("");
    setIsPhysicalPosCouponLoading(false);
  }, [physicalPosCouponCode, dailyPrice, paymentType, selectedService, packageCount, usePhysicalPosCoupon]);

  useEffect(() => {
    if (!token) return;
    const customerID = localStorage.getItem("customerID");
    if (!customerID) return;

    Services.GetUsersBillingAddresses(token, customerID)
      .then((res) => {
        if (!res.data || !res.data.status || !Array.isArray(res.data.result)) {
          return;
        }
        const mapped = res.data.result.map((item) => {
          const postalFromApi = item.postalCode || "";
          const postalFromAddress = extractPostalCodeFromAddress(item.address);
          const postalCodeVal = postalFromApi || postalFromAddress || "";

          return {
            id: item.id || generateRandomCaseSensitiveString(10),
            label: item.title || "Adres",
            country: item.country || "Türkiye",
            addressLine: item.street || item.address || "",
            city: item.city || "",
            district: item.neighbour || item.region || "",
            postalCode: postalCodeVal
          };
        });
        setSavedAddresses(mapped);
      })
      .catch((err) => { });
  }, [token]);

  useEffect(() => {
    if (currentPaymentType === "withCreditCard" || isOfflinePayment) {
      const storageEmail = localStorage.getItem("email");
      const storagePhone = localStorage.getItem("phoneNumber");
      if (storageEmail) setEmail(storageEmail);
      if (storagePhone) setPhoneNumber(storagePhone);
    }
  }, [currentPaymentType, isOfflinePayment]);

  function extractPostalCodeFromAddress(address) {
    if (!address) return "";
    const match = address.match(/\((\d{4,6})\)\s*$/);
    return match ? match[1] : "";
  }

  const pad2 = (n) => String(n).padStart(2, "0");
  const formatDateTime = (date) => {
    const yyyy = date.getFullYear();
    const mm = pad2(date.getMonth() + 1);
    const dd = pad2(date.getDate());
    const HH = pad2(date.getHours());
    const MM = pad2(date.getMinutes());
    return `${yyyy}-${mm}-${dd} ${HH}:${MM}:00`;
  };

  function getTodayAtTenPM() {
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 22, 0, 0, 0);
    return formatDateTime(d);
  }

  function getWillDateDeliverDateTimeByDurationKey(key) {
    if (!key || key === "-" || selectedService === "gift") {
      return getTodayAtTenPM();
    }
    const now = new Date();
    const result = new Date(now.getTime());

    if (key === "30 DK") {
      result.setMinutes(result.getMinutes() + 30);
      return formatDateTime(result);
    }

    if (key === "1 Gün") {
      result.setHours(result.getHours() + 24);
    } else {
      const hrs = parseInt(key, 10);
      if (!isNaN(hrs) && hrs > 0) {
        result.setHours(result.getHours() + hrs);
      } else {
        return getTodayAtTenPM();
      }
    }
    return formatDateTime(result);
  }

  const secondsFromKey = (key) => {
    if (!key) return 3600;

    if (key === "30 DK") return 30 * 60;

    if (key === "1 Gün") return 24 * 3600;
    const hrs = parseInt(key, 10);
    return Number.isFinite(hrs) && hrs > 0 ? hrs * 3600 : 3600;
  };
  const handlePhoneChange = (e) => {
    const input = e.target.value;
    if (input.length <= 10) setPhoneNumber(input);
  };

  const handleTcknChange = (e) => {
    const inputValue = e.target.value;
    if (inputValue.length <= 11) setTckn(inputValue);
  };

  const validateTCKN = (value) => {
    value = String(value);
    if (!/^[0-9]{11}$/.test(value)) return false;

    if (value[0] === '0') return false;

    let oddSum = 0;
    let evenSum = 0;
    let totalSum = 0;

    for (let i = 0; i < 9; i++) {
      const digit = parseInt(value[i]);
      if (i % 2 === 0) {
        oddSum += digit;
      } else {
        evenSum += digit;
      }
      totalSum += digit;
    }
    totalSum += parseInt(value[9]);

    const calculatedDigit10 = ((oddSum * 7 - evenSum) % 10 + 10) % 10;
    const calculatedDigit11 = totalSum % 10;

    return calculatedDigit10 === parseInt(value[9]) && calculatedDigit11 === parseInt(value[10]);
  };

  const shouldShowTckn = phoneCountryCode === "+90";

  const isCardOrPosPayment = currentPaymentType === "withCreditCard" || isOfflinePayment;
  const getRequestType = () => {
    if (selectedService === "gift") return "gift";
    if (selectedService === "cocuk" || selectedService === "kid-car") return "kid-car";
    return "pet-car";
  };
  const validateCouponForTotal = (couponResult, totalPrice) => {
    const discountValue = Number(couponResult.discountValue) || 0;
    let discountAmount = 0;
    if (couponResult.discountType === "percentage") {
      discountAmount = (totalPrice * discountValue) / 100;
    } else if (couponResult.discountType === "amount") {
      discountAmount = discountValue;
    }
    let finalPrice = totalPrice - discountAmount;
    if (finalPrice < 0) finalPrice = 0;
    return { discountAmount, finalPrice };
  };

  const emailError = (isCardOrPosPayment && loginMethod !== 'phone' && !email)
    ? getTranslation("validationEmailRequired")
    : "";

  const phoneError = (isCardOrPosPayment && phoneNumber.length !== 10)
    ? getTranslation("validationPhoneLength")
    : "";

  let tcknError = "";
  if (isCardOrPosPayment && shouldShowTckn) {
    if (!tckn) {
      tcknError = getTranslation("validationTcknRequired") || "TC Kimlik No zorunludur";
    } else if (tckn.length !== 11) {
      tcknError = getTranslation("validationTcknLength") || "11 hane olmalı";
    } else if (!validateTCKN(tckn)) {
      tcknError = "Geçersiz T.C. Kimlik Numarası";
    }
  }

  const addressError = isCardOrPosPayment && wantsInvoice && !address ? getTranslation("validationAddressRequired") : "";
  const cityError = isCardOrPosPayment && wantsInvoice && !city ? getTranslation("validationCityRequired") : "";
  const districtError = isCardOrPosPayment && wantsInvoice && !district ? getTranslation("validationDistrictRequired") : "";
  const postalCodeError = isCardOrPosPayment && wantsInvoice && !postalCode ? getTranslation("validationPostalCodeRequired") : "";
  const countryError = isCardOrPosPayment && wantsInvoice && !country ? getTranslation("validationCountryRequired") : "";

  const firstNameError = currentPaymentType === "withCoupon" && !firstName ? getTranslation("validationNameRequired") || "İsim gerekli" : "";
  const lastNameError = currentPaymentType === "withCoupon" && !lastName ? getTranslation("validationSurnameRequired") || "Soyisim gerekli" : "";
  const couponError = currentPaymentType === "withCoupon" && !couponCode ? getTranslation("validationCouponRequired") || "Kupon zorunlu" : "";

  const allFieldsAreValid = () => {
    if (currentPaymentType === "withCoupon") {
      return !firstNameError && !lastNameError && !couponError;
    }

    if (wantsInvoice) {
      return !emailError && !phoneError && !tcknError && !addressError && !cityError && !districtError && !postalCodeError && !countryError;
    }

    return !emailError && !phoneError && !tcknError;
  };

  const getCommonAgreementData = () => {
    let displayedAddress = "";

    if (wantsInvoice) {
      displayedAddress = `${address} ${city}/${district}`.trim();
    } else {
      const mTitle = mallInfo?.mallTitle || "";
      const mAddr1 = mallInfo?.muhaberatAddress1 || "";
      const mCity = mallInfo?.city || "";
      const mDist = mallInfo?.district || "";
      const mZip = mallInfo?.postalCode || "";

      displayedAddress = `${mTitle} ${mAddr1} ${mDist}/${mCity} ${mZip}`.trim();
    }

    const finalTckn = tckn || "11111111111";

    return {
      name: localStorage.getItem("name"),
      lastname: localStorage.getItem("lastname"),
      tckn: finalTckn,
      address: displayedAddress,
      phone: localStorage.getItem("phoneNumber") || (phoneCountryCode + phoneNumber),
      email: email || localStorage.getItem("email"),
      dailyPrice
    };
  };

  const handleOpenDocument = (docType) => {
    const agreementData = getCommonAgreementData();

    if (docType === "agreement" || docType === "preInfo") {
      const { ui } = getAgreementTexts(agreementData);
      if (docType === "agreement") {
        setViewingDocumentTitle("Çocuk Arabası Mesafeli Hizmet Sözleşmesi");
        setViewingDocumentHtml(ui[2]);
      } else {
        setViewingDocumentTitle("Çocuk Arabası Ön Bilgilendirme Formu");
        setViewingDocumentHtml(ui[1]);
      }
      return;
    }

    if (docType === "petAgreement" || docType === "petPreInfo") {
      const { ui } = getPetAgreementTexts(agreementData);
      if (docType === "petAgreement") {
        setViewingDocumentTitle("Evcil Hayvan Aracı Mesafeli Hizmet Sözleşmesi");
        setViewingDocumentHtml(ui[0]);
      } else {
        setViewingDocumentTitle("Evcil Hayvan Aracı Ön Bilgilendirme Formu");
        setViewingDocumentHtml(ui[1]);
      }
      return;
    }

    if (docType === "kvkk") {
      setViewingDocumentTitle(loginDocuments.kvkk.title);
      setViewingDocumentHtml(loginDocuments.kvkk.content);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!allFieldsAreValid()) return;

    if (selectedService === "cocuk" || selectedService === "kid-car") {
      if (!isCancellationPolicyAccepted) { setShowCancellationError(true); return; }
      if (!isKidCombinedAgreementAccepted) { setShowAgreementError(true); return; }
    }
    if (selectedService === "hayvan" || selectedService === "pet-car") {
      if (!isPetCombinedAgreementAccepted) { setShowAgreementError(true); return; }
    }

    const fullPhone = localStorage.getItem("phoneNumber") || (phoneCountryCode + phoneNumber || "-");
    const customerID = localStorage.getItem("customerID");
    const reqType = getRequestType();

    const willDateDeliverDateTime = reqType !== "gift" ? getWillDateDeliverDateTimeByDurationKey(selectedDurationKey) : getTodayAtTenPM();
    const currency = "TL";

    if (type === "extend") {
      if (reqType === "gift") return;
      const baseTs = Number(originalWillDeliverDateTime) || 0;
      const extSeconds = secondsFromKey(selectedDurationKey);
      const newWillDeliverDateTimeTs = baseTs + extSeconds;
      const serviceObjForPayment = {
        price: Number(dailyPrice) || 0,
        billing: { phone: localStorage.getItem("phoneNumber") || fullPhone }
      };
      navigate("/payment-method", {
        state: {
          selectedService,
          dailyPrice: Number(dailyPrice) || 0,
          packageCount,
          paymentType: "withCreditCard",
          conversationID: requestID,
          isExtend: true,
          requestID,
          extendSeconds: extSeconds,
          newWillDeliverDateTimeTs,
          serviceObj: serviceObjForPayment
        }
      });
      return;
    }

    let finalAddress = "";
    let finalCity = "";
    let finalDistrict = "";
    let finalPostalCode = "";
    let finalCountry = "Türkiye";

    if (wantsInvoice) {
      finalAddress = address;
      finalCity = city;
      finalDistrict = district;
      finalPostalCode = postalCode;
      finalCountry = country;
    } else {
      const mTitle = mallInfo?.mallTitle || "";
      const mAddr1 = mallInfo?.muhaberatAddress1 || "";

      finalAddress = `${mTitle} ${mAddr1}`.trim();
      if (!finalAddress) finalAddress = "AVM Adresi Belirtilmemiş";

      finalCity = mallInfo?.city || "-";
      finalDistrict = mallInfo?.district || "-";
      finalPostalCode = mallInfo?.postalCode || "";
    }

    const finalTckn = tckn || "11111111111";

    const billing = {
      name: localStorage.getItem("name"),
      lastname: localStorage.getItem("lastname"),
      email: email || localStorage.getItem("email") || "-",
      address: finalAddress,
      city: finalCity,
      town: finalDistrict,
      phone: fullPhone,
      country: finalCountry,
      tckn: finalTckn,
      postalCode: finalPostalCode
    };

    let approvedAgreements = null;
    const agreementData = getCommonAgreementData();

    if (reqType === "kid-car") {
      approvedAgreements = getConfirmedAgreementTexts({ serviceType: "kid-car", data: agreementData });
    } else if (reqType === "pet-car" || selectedService === "hayvan") {
      approvedAgreements = getConfirmedAgreementTexts({ serviceType: "pet-car", data: agreementData });
    }

    let activePhysicalPosCoupon = null;
    if (isOfflinePayment && usePhysicalPosCoupon) {
      if (!physicalPosCouponCode) {
        setPhysicalPosCouponMessage(getTranslation("validationCouponRequired") || "Kupon zorunlu");
        return;
      }

      if (isPhysicalPosCouponValidated && physicalPosCouponData) {
        activePhysicalPosCoupon = physicalPosCouponData;
      } else {
        setIsPhysicalPosCouponLoading(true);
        setPhysicalPosCouponMessage("");
        try {
          const validateRes = await Services.ValidateCoupon(token, physicalPosCouponCode, reqType);
          if (!validateRes?.data?.status) {
            setPhysicalPosCouponMessage("Kupon kodu geçersizdir, lütfen kontrol edin.");
            return;
          }
          const c = validateRes.data.result;
          const { discountAmount, finalPrice } = validateCouponForTotal(c, baseTotalPrice);
          activePhysicalPosCoupon = { ...c, discountAmount, finalPrice };
          setPhysicalPosCouponData(activePhysicalPosCoupon);
          setPhysicalPosDiscountedPrice(finalPrice);
          setIsPhysicalPosCouponValidated(true);
          return;
        } catch (err) {
          setPhysicalPosCouponMessage("Kupon kodu geçersizdir, lütfen kontrol edin.");
          console.log(err);
          return;
        } finally {
          setIsPhysicalPosCouponLoading(false);
        }
      }
    }

    const hasCouponLeftover = hasCouponApplied && discountedPrice !== null && discountedPrice > 0;
    const hasPhysicalPosCouponApplied = isOfflinePayment && usePhysicalPosCoupon && !!activePhysicalPosCoupon;
    let priceForOrder;
    let totalForGift = 0;
    let priceForPayment;

    if (selectedService === "gift") {
      if (hasPhysicalPosCouponApplied) {
        const total = activePhysicalPosCoupon.finalPrice;
        const perUnit = packageCount && packageCount > 0 ? total / packageCount : total;
        priceForOrder = perUnit;
        totalForGift = total;
        priceForPayment = perUnit;
      } else if (hasCouponLeftover) {
        const total = discountedPrice;
        const perUnit = packageCount && packageCount > 0 ? total / packageCount : total;
        priceForOrder = perUnit;
        totalForGift = total;
        priceForPayment = perUnit;
      } else {
        const base = Number(dailyPrice) || 0;
        priceForOrder = base;
        totalForGift = base * (packageCount || 1);
        priceForPayment = base;
      }
    } else {
      if (hasPhysicalPosCouponApplied) {
        priceForOrder = activePhysicalPosCoupon.finalPrice;
      } else if (hasCouponLeftover) {
        priceForOrder = discountedPrice;
      } else {
        priceForOrder = Number(dailyPrice) || 0;
      }
      priceForPayment = priceForOrder;
    }

    let duration = 0;
    if (selectedDurationKey === "30 DK") {
      duration = 1800; 
    } else if (selectedDurationKey === "1 Gün") {
      duration = 86400; 
    } else {      
      duration = parseInt(selectedDurationKey, 10) * 3600;
    }

    const paymentTypeForOrder = currentPaymentType === "withCoupon"
      ? "coupon"
      : isPhysicalPosPayment
        ? "physical-pos"
        : isCashPayment
          ? "cash"
        : "online-payment";
    const couponCodeForOrder = currentPaymentType === "withCoupon"
      ? (couponCode ? couponCode : false)
      : (hasPhysicalPosCouponApplied ? physicalPosCouponCode : false);

    if (isOfflinePayment && isSubmittingRequest) return;

    const apiCall = reqType !== "gift"
      ? Services.CreateOrder(
        token, poiID, customerID, reqType, willDateDeliverDateTime, priceForOrder, currency, billing,
        selectedDurationKey, duration, couponCodeForOrder,
        approvedAgreements, userIP, paymentTypeForOrder
      )
      : Services.CreateOrderForGift(
        token, poiID, customerID, reqType, packageCount, totalForGift, currency, billing,
        couponCodeForOrder, paymentTypeForOrder
      );

    apiCall
      .then(async (res) => {
        if (!res.data.status) {
          console.log("Error:", res.data);
          return;
        }

        if (isOfflinePayment) {
          if (hasPhysicalPosCouponApplied) {
            try {
              const usedCustomerID = localStorage.getItem("customerID") || "";
              const usedName = localStorage.getItem("name") || "";
              const usedLastName = localStorage.getItem("lastname") || "";
              const usedMallTitle = mallInfo?.mallTitle || localStorage.getItem("mallTitle") || "";
              const usedRequestID = res?.data?.result?.requestID || requestID || "";
              const transactionAmount = Number(baseTotalPrice) || 0;
              const netAmount = Number(activePhysicalPosCoupon?.finalPrice ?? transactionAmount) || 0;
              await Services.UseCoupon(
                token,
                physicalPosCouponCode,
                reqType,
                usedCustomerID,
                usedName,
                usedLastName,
                usedMallTitle,
                usedRequestID,
                transactionAmount,
                netAmount
              );
            } catch (err) {
              console.log("UseCoupon error:", err);
            }
          }
          setSuccessMessage(
            getTranslation("physicalPosConfirmation") ||
            "Talebiniz alınmıştır. Ödeme için Easy Point Desk'ten destek alabilirsiniz."
          );
          setShowSuccessModal(true);
          return;
        }

        const paymentDailyPrice = selectedService === "gift" ? totalForGift : priceForOrder;
        navigate("/payment-method", {
          state: {
            selectedService,
            dailyPrice: paymentDailyPrice,
            packageCount,
            serviceObj: { price: priceForPayment, billing },
            conversationID: res.data.result.requestID,
            requestID: res.data.result.requestID,
            paymentType: "withCreditCard",
            confirmedAgreementText: approvedAgreements,
            confirmedAgreementIP: userIP,
            ...(hasCouponLeftover && couponData ? { appliedCoupon: { code: couponCode, discountType: couponData.discountType, discountValue: couponData.discountValue, discountAmount: couponData.discountAmount, finalPrice: couponData.finalPrice } } : {})
          }
        });
      })
      .catch((err) => console.log(err))
      .finally(() => {
        if (isOfflinePayment) {
          setIsSubmittingRequest(false);
        }
      });

    if (isOfflinePayment) {
      setIsSubmittingRequest(true);
    }
  };

  const handleCouponFlow = (e) => {
    e.preventDefault();
    if (!allFieldsAreValid()) return;

    const customerID = localStorage.getItem("customerID");
    const reqType = getRequestType();

    if (!isCouponValidated) {
      setIsCouponLoading(true);
      setCouponValidationMessage("");
      Services.ValidateCoupon(token, couponCode, reqType)
        .then((res) => {
          if (res.data.status) {
            const c = res.data.result;
            const discountValue = Number(c.discountValue) || 0;
            let discountAmount = 0;
            if (c.discountType === "percentage") {
              discountAmount = (baseTotalPrice * discountValue) / 100;
            } else if (c.discountType === "amount") {
              discountAmount = discountValue;
            }
            let finalPrice = baseTotalPrice - discountAmount;
            if (finalPrice < 0) finalPrice = 0;
            setCouponData({ ...c, discountAmount, finalPrice });
            setDiscountedPrice(finalPrice);
            setIsCouponValidated(true);
            setHasCouponApplied(true);
            setEffectivePrice(finalPrice);
          } else {
            setCouponValidationMessage("Kupon kodu geçersizdir, lütfen kontrol edin.");
            console.log("Error:", res.data);
          }
        })
        .catch((err) => {
          setCouponValidationMessage("Kupon kodu geçersizdir, lütfen kontrol edin.");
          console.log(err);
        })
        .finally(() => {
          setIsCouponLoading(false);
        });
      return;
    }

    if (!couponData || discountedPrice === null) return;
    if (type === "extend") return;

    if (discountedPrice === 0) {
      if (isCouponCompleting) return;
      if (selectedService === "cocuk" || selectedService === "kid-car") {
        if (!isCancellationPolicyAccepted) { setShowCancellationError(true); return; }
        if (!isKidCombinedAgreementAccepted) { setShowAgreementError(true); return; }
      }
      if (selectedService === "hayvan" || selectedService === "pet-car") {
        if (!isPetCombinedAgreementAccepted) { setShowAgreementError(true); return; }
      }

      const currency = "TL";
      const willDateDeliverDateTime = reqType !== "gift" ? getWillDateDeliverDateTimeByDurationKey(selectedDurationKey) : getTodayAtTenPM();
      const priceForOrderZero = 0;
      let duration = 0;
      if (selectedDurationKey === "30 DK") duration = 1800;
      else if (selectedDurationKey === "1 Gün") duration = 86400;
      else duration = parseInt(selectedDurationKey, 10) * 3600;

      const finalTckn = tckn || "11111111111";

      const billingForCoupon = {
        name: firstName || localStorage.getItem("name"),
        lastname: lastName || localStorage.getItem("lastname"),
        email: localStorage.getItem("email") || "",
        address: mallInfo?.address || "-",
        city: mallInfo?.city || "-",
        town: mallInfo?.district || "-",
        phone: localStorage.getItem("phoneNumber") || "",
        country: "Türkiye",
        tckn: finalTckn,
        postalCode: ""
      };

      let approvedAgreementsZero = null;
      const agreementData = getCommonAgreementData();
      if (reqType === "kid-car") approvedAgreementsZero = getConfirmedAgreementTexts({ serviceType: "kid-car", data: agreementData });
      else if (reqType === "pet-car") approvedAgreementsZero = getConfirmedAgreementTexts({ serviceType: "pet-car", data: agreementData });

      const paymentTypeForOrderZero = "coupon";

      const apiCallZero = reqType !== "gift"
        ? Services.CreateOrder(
          token, poiID, customerID, reqType, willDateDeliverDateTime, priceForOrderZero, currency, billingForCoupon,
          selectedDurationKey, duration, couponCode ? couponCode : false,
          approvedAgreementsZero, userIP, paymentTypeForOrderZero
        )
        : Services.CreateOrderForGift(
          token, poiID, customerID, reqType, packageCount, priceForOrderZero, currency, billingForCoupon, couponCode ? couponCode : false, paymentTypeForOrderZero
        );

      apiCallZero
        .then(async (res) => {
          if (!res.data.status) { console.log("Error:", res.data); return; }
          const usedRequestID = res?.data?.result?.requestID || requestID || "";
          const couponPaymentID = usedRequestID ? `CPN-${usedRequestID}` : `CPN-${Date.now()}`;

          try {
            const usedCustomerID = localStorage.getItem("customerID") || "";
            const usedName = firstName || localStorage.getItem("name") || "";
            const usedLastName = lastName || localStorage.getItem("lastname") || "";
            const usedMallTitle = mallInfo?.mallTitle || localStorage.getItem("mallTitle") || "";
            const transactionAmount = Number(baseTotalPrice) || 0;
            const netAmount = Number(discountedPrice) || 0;

            if (usedRequestID) {
              await Services.SetPaid(token, usedRequestID, couponPaymentID, "0000");
            }
            await Services.UseCoupon(token, couponCode, reqType, usedCustomerID, usedName, usedLastName, usedMallTitle, usedRequestID, transactionAmount, netAmount);
          } catch (err) { }
          setShowSuccessModal(true);
        })
        .catch((err) => console.log(err))
        .finally(() => setIsCouponCompleting(false));
      setIsCouponCompleting(true);
      return;
    }
    setCurrentPaymentType("withCreditCard");
  };

  const proceedToNextStep = () => {
    if (selectedService === "cocuk" || selectedService === "kid-car") {
      let hasError = false;
      if (!isCancellationPolicyAccepted) {
        setShowCancellationError(true);
        hasError = true;
      }
      if (!isKidCombinedAgreementAccepted) {
        setShowAgreementError(true);
        hasError = true;
      }
      if (hasError) return;
    }

    if (selectedService === "hayvan" || selectedService === "pet-car") {
      if (!isPetCombinedAgreementAccepted) {
        setShowAgreementError(true);
        return;
      }
    }

    handleSubmit(new Event("submit"));
  };

  const handleContinueClick = (e) => {
    if (currentPaymentType === "withCoupon") {
      return handleCouponFlow(e);
    }
    e.preventDefault();
    if (!allFieldsAreValid()) return;

    if (wantsInvoice && saveAddress && !isSaveAddressModalOpen) {
      setIsSaveAddressModalOpen(true);
      return;
    }

    proceedToNextStep();
  };

  const handleSaveAddressConfirm = async () => {
    const customerID = localStorage.getItem("customerID") || "-";
    const id = generateRandomCaseSensitiveString(20);
    const fullPhone = localStorage.getItem("phoneNumber") || (phoneCountryCode + phoneNumber || "-");
    const safeEmail = email || localStorage.getItem("email") || "-";
    const title = addressName || "-";
    const street = address || "-";
    const neighbour = district || "-";
    const region = city || "-";
    const cityVal = city || "-";
    const countryVal = country || "-";
    const fullAddress = address || city || district ? `${address} ${city}/${district} ${postalCode ? `(${postalCode})` : ""}`.trim() : "-";

    try {
      await Services.AddUserBillingAddress(
        token, id, customerID, title, street, neighbour, region, cityVal, countryVal, fullPhone, fullAddress, "11111111111", safeEmail, "-"
      );
    } catch (err) {
      console.log("AddUserBillingAddress error:", err);
    }
    setIsSaveAddressModalOpen(false);
    proceedToNextStep();
  };

  const isAgreementStepIncomplete =
    (selectedService === "cocuk" || selectedService === "kid-car")
      ? (!isCancellationPolicyAccepted || !isKidCombinedAgreementAccepted)
      : (selectedService === "hayvan" || selectedService === "pet-car")
        ? !isPetCombinedAgreementAccepted
        : false;

  const isButtonDisabled =
    !allFieldsAreValid()
    || isAgreementStepIncomplete
    || (currentPaymentType === "withCoupon" && (isCouponLoading || isCouponCompleting))
    || (isOfflinePayment && (isPhysicalPosCouponLoading || isSubmittingRequest));

  return (
    <div className="rent-confirmation-container">
      <div className="header-wrapper">
        <BackButton location="/mainpage" />
      </div>

      <div className="rent-confirmation-header">
        <span className="rent-confirmation-title">
          {currentPaymentType === "withCoupon"
            ? getTranslation("confirmCouponTitle") || "Kupon Kullanımı"
            : getTranslation("confirmRentalInfo")}
        </span>
        <span className="rent-confirmation-subtitle">
          {currentPaymentType === "withCoupon"
            ? getTranslation("confirmCouponSubtitle") || "Bilgileri doğrulayın ve devam edin"
            : getTranslation("confirmAndVerifyInfo")}
        </span>
      </div>

      <form className="rent-confirmation-form" onSubmit={currentPaymentType === "withCoupon" ? handleCouponFlow : handleSubmit} noValidate>
        {currentPaymentType === "withCoupon" ? (
          <>
            <InputField value={firstName} onChange={(e) => setFirstName(e.target.value)} label={getTranslation("name") || "İsim"} placeholder={getTranslation("name") || "Adınız"} />
            {firstNameError && <span className="validation-error">{firstNameError}</span>}
            <InputField value={lastName} onChange={(e) => setLastName(e.target.value)} label={getTranslation("lastname") || "Soyisim"} placeholder={getTranslation("lastname") || "Soyadınız"} />
            {lastNameError && <span className="validation-error">{lastNameError}</span>}
            <InputField value={couponCode} onChange={(e) => setCouponCode(e.target.value)} label={getTranslation("couponCode") || "Kupon Kodu"} placeholder={getTranslation("couponCode") || "Örn: ABC123"} />
            {couponError && <span className="validation-error">{couponError}</span>}
            {couponValidationMessage && <span className="validation-error">{couponValidationMessage}</span>}
            {couponData && (
              <div className="coupon-summary">
                <div className="coupon-summary-row"><span>{getTranslation("originalPriceLabel") || "Kupon öncesi tutar"}</span><span>{baseTotalPrice.toFixed(2)} TL</span></div>
                <div className="coupon-summary-row"><span>{getTranslation("discountLabel") || "İndirim"} {couponData.discountType === "percentage" ? `(%${couponData.discountValue})` : `(${couponData.discountValue} TL)`}</span><span>-{Math.min(baseTotalPrice, couponData.discountAmount).toFixed(2)} TL</span></div>
                <div className="coupon-summary-row coupon-summary-final"><span>{getTranslation("amountToPayLabel") || "Ödenecek Tutar"}</span><span>{(discountedPrice ?? 0).toFixed(2)} TL</span></div>
              </div>
            )}
          </>
        ) : (
          <>
            {loginMethod !== 'phone' && (
              <>
                <InputField value={email} onChange={(e) => setEmail(e.target.value)} label={getTranslation("emailAddressLabel")} placeholder={getTranslation("emailAddressPlaceholder")} />
                {emailError && <span className="validation-error">{emailError}</span>}
              </>
            )}

            <PhoneInput value={phoneNumber} handlePhoneChange={handlePhoneChange} onCountryChange={setPhoneCountryCode} label={getTranslation("phoneNumber") + "*"} placeholder="5XXXXXXXXX" />
            {phoneError && <span className="validation-error">{phoneError}</span>}

            {shouldShowTckn && (
              <>
                <InputField type="number" onChange={handleTcknChange} value={tckn} label={getTranslation("nationalId")} placeholder={getTranslation("nationalIdPlaceholder")} />
                {tcknError && <span className="validation-error">{tcknError}</span>}
              </>
            )}

            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
                {getTranslation("invoicePreferenceLabel") || "Fatura Tercihi"}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setWantsInvoice((prev) => !prev)}>
                <input type="checkbox" checked={wantsInvoice} onChange={() => { }} style={{ width: '20px', height: '20px', marginRight: '8px', cursor: 'pointer' }} />
                <span style={{ fontSize: '14px' }}>{getTranslation("wantCorporateInvoice") || "Kurumsal Fatura İstiyorum"}</span>
              </div>
            </div>

            {wantsInvoice && (
              <div style={{ marginTop: '15px' }}>
                <AddressSelectBar
                  addresses={savedAddresses}
                  selectedAddressId={selectedSavedAddressId}
                  placeholder={getTranslation("addressSelectPlaceholder")}
                  onSelect={(addr) => {
                    setSelectedSavedAddressId(addr.id);
                    setCountry(addr.country || "Türkiye");
                    setAddress(addr.addressLine || "");
                    setCity(addr.city || "");
                    setDistrict(addr.district || "");
                    setPostalCode(addr.postalCode || "");
                  }}
                  onClear={() => setSelectedSavedAddressId(null)}
                />

                <InputField onChange={(e) => setCountry(e.target.value)} value={country} label={getTranslation("countryLabel")} placeholder={getTranslation("countryPlaceholder")} disabled={isUsingSavedAddress} />
                {countryError && <span className="validation-error">{countryError}</span>}
                <InputField onChange={(e) => setAddress(e.target.value)} value={address} label={getTranslation("streetAddressLabel")} placeholder={getTranslation("streetAddressPlaceholder")} disabled={isUsingSavedAddress} />
                {addressError && <span className="validation-error">{addressError}</span>}
                <InputField onChange={(e) => setCity(e.target.value)} value={city} label={getTranslation("cityLabel")} placeholder={getTranslation("cityPlaceholder")} disabled={isUsingSavedAddress} />
                {cityError && <span className="validation-error">{cityError}</span>}
                <InputField style={{ width: "100%" }} onChange={(e) => setDistrict(e.target.value)} value={district} label={getTranslation("districtLabel")} placeholder={getTranslation("districtPlaceholder")} disabled={isUsingSavedAddress} />
                {districtError && <span className="validation-error">{districtError}</span>}
                <InputField style={{ width: "100%" }} onChange={(e) => setPostalCode(e.target.value)} value={postalCode} label={getTranslation("postalCodeLabel")} placeholder={getTranslation("postalCodePlaceholder")} disabled={isUsingSavedAddress} />
                {postalCodeError && <span className="validation-error">{postalCodeError}</span>}

                {!isUsingSavedAddress && (
                  <div className="save-address-row">
                    <Checkbox checked={saveAddress} onChange={setSaveAddress} />
                    <span className="save-address-label" onClick={() => setSaveAddress((prev) => !prev)}>
                      {getTranslation("saveAddressLabel") || "Adresimi kaydet"}
                    </span>
                  </div>
                )}
              </div>
            )}

          </>
        )}

        {isOfflinePayment && (
          <>
            <div className="save-address-row">
              <Checkbox checked={usePhysicalPosCoupon} onChange={setUsePhysicalPosCoupon} />
              <span className="save-address-label" onClick={() => setUsePhysicalPosCoupon((prev) => !prev)}>
                {getTranslation("useCouponCode") || "Kupon Kodu Kullan"}
              </span>
            </div>
            {usePhysicalPosCoupon && (
              <>
                <InputField
                  value={physicalPosCouponCode}
                  onChange={(e) => setPhysicalPosCouponCode(e.target.value)}
                  label={getTranslation("couponCode") || "Kupon Kodu"}
                  placeholder={getTranslation("couponCode") || "Örn: ABC123"}
                />
                {physicalPosCouponMessage && <span className="validation-error">{physicalPosCouponMessage}</span>}
                {physicalPosCouponData && (
                  <div className="coupon-summary">
                    <div className="coupon-summary-row"><span>{getTranslation("originalPriceLabel") || "Kupon öncesi tutar"}</span><span>{baseTotalPrice.toFixed(2)} TL</span></div>
                    <div className="coupon-summary-row"><span>{getTranslation("discountLabel") || "İndirim"} {physicalPosCouponData.discountType === "percentage" ? `(%${physicalPosCouponData.discountValue})` : `(${physicalPosCouponData.discountValue} TL)`}</span><span>-{Math.min(baseTotalPrice, physicalPosCouponData.discountAmount).toFixed(2)} TL</span></div>
                    <div className="coupon-summary-row coupon-summary-final"><span>{getTranslation("amountToPayLabel") || "Ödenecek Tutar"}</span><span>{(physicalPosDiscountedPrice ?? 0).toFixed(2)} TL</span></div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {(selectedService === "cocuk" || selectedService === "kid-car") && (
          <>
            <div className="cancellation-policy-block">
              <div className="cancellation-policy-row">
                <Checkbox
                  checked={isCancellationPolicyAccepted}
                  onChange={(checked) => {
                    setIsCancellationPolicyAccepted(checked);
                    if (checked) setShowCancellationError(false);
                  }}
                />
                <span
                  className="cancellation-policy-checkbox-label"
                  onClick={() => {
                    setIsCancellationPolicyAccepted(prev => !prev);
                    if (!isCancellationPolicyAccepted) setShowCancellationError(false);
                  }}
                >
                  Hizmetin ifasına (çocuk arabasının teslimine) başlanmasından sonra cayma hakkımın bulunmadığını ve erken iade halinde ücret iadesi yapılmayacağını kabul ediyorum.
                </span>
              </div>
              {showCancellationError && (
                <span style={{ color: '#F04438', fontSize: '12px', marginLeft: '28px' }}>
                  Lütfen cayma hakkı bilgilendirmesini onaylayınız.
                </span>
              )}
            </div>

            <div className="cancellation-policy-block">
              <div className="cancellation-policy-row">
                <Checkbox
                  checked={isKidCombinedAgreementAccepted}
                  onChange={(checked) => {
                    setIsKidCombinedAgreementAccepted(checked);
                    if (checked) setShowAgreementError(false);
                  }}
                />
                <span className="cancellation-policy-checkbox-label">
                  <span
                    className="cancellation-policy-checkbox-label-link"
                    onClick={() => handleOpenDocument("preInfo")}
                  >
                    Önbilgilendirme Formu
                  </span>
                  {" ve "}
                  <span
                    className="cancellation-policy-checkbox-label-link"
                    onClick={() => handleOpenDocument("agreement")}
                  >
                    Mesafeli Hizmet Sözleşmesini
                  </span>
                  {" okudum, onaylıyorum."}
                </span>
              </div>
              {showAgreementError && (
                <span style={{ color: '#F04438', fontSize: '12px', marginLeft: '28px' }}>
                  Lütfen sözleşmeleri onaylayınız.
                </span>
              )}
            </div>
          </>
        )}

        {(selectedService === "hayvan" || selectedService === "pet-car") && (
          <>
            <div className="cancellation-policy-block">
              <div className="cancellation-policy-row">
                <Checkbox
                  checked={isPetCombinedAgreementAccepted}
                  onChange={(checked) => {
                    setIsPetCombinedAgreementAccepted(checked);
                    if (checked) setShowAgreementError(false);
                  }}
                />
                <span className="cancellation-policy-checkbox-label">
                  <span
                    className="cancellation-policy-checkbox-label-link"
                    onClick={() => handleOpenDocument("petPreInfo")}
                  >
                    Önbilgilendirme Formu
                  </span>
                  {" ve "}
                  <span
                    className="cancellation-policy-checkbox-label-link"
                    onClick={() => handleOpenDocument("petAgreement")}
                  >
                    Mesafeli Hizmet Sözleşmesini
                  </span>
                  {" okudum, onaylıyorum."}
                </span>
              </div>
              {showAgreementError && (
                <span style={{ color: '#F04438', fontSize: '12px', marginLeft: '28px' }}>
                  Lütfen sözleşmeleri onaylayınız.
                </span>
              )}
            </div>
          </>
        )}

        <Button
          onClick={handleContinueClick}
          disabled={isButtonDisabled}
          icon={(currentPaymentType === "withCoupon" && isCouponLoading) || (isOfflinePayment && (isPhysicalPosCouponLoading || isSubmittingRequest)) ? <div className="btn-spinner" /> : <TbCreditCard size={22} />}
          label={
            currentPaymentType === "withCoupon"
              ? isCouponLoading
                ? getTranslation("validatingCoupon") || "Kupon Doğrulanıyor..."
                : isCouponCompleting
                  ? getTranslation("completingWithoutPayment") || "İşlem Tamamlanıyor..."
                : isCouponValidated
                  ? discountedPrice === 0
                    ? getTranslation("completeWithoutPayment") || "İşlemi Tamamla"
                    : getTranslation("goToPayment") || "Ödemeye Geç"
                  : getTranslation("validateCoupon") || "Kuponu Doğrula"
              : isOfflinePayment
                ? isPhysicalPosCouponLoading
                  ? getTranslation("validatingCoupon") || "Kupon Doğrulanıyor..."
                  : isSubmittingRequest
                    ? getTranslation("sendingRentalRequest") || "Kiralama Talebi Gönderiliyor..."
                  : usePhysicalPosCoupon && !isPhysicalPosCouponValidated
                    ? getTranslation("validateCoupon") || "Kuponu Doğrula"
                    : getTranslation("sendRentalRequest") || "Kiralama Talebi Gönder"
                : getTranslation("continueToPayment")
          }
          style={{ width: "100%", marginTop: "18px" }}
        />

        <SaveAddressModal
          isOpen={isSaveAddressModalOpen}
          onClose={() => setIsSaveAddressModalOpen(false)}
          onConfirm={handleSaveAddressConfirm}
          addressName={addressName}
          setAddressName={setAddressName}
        />

        {viewingDocumentHtml && (
          <div className="rent-document-modal-root">
            <div className="rent-document-modal-overlay" onClick={() => setViewingDocumentHtml(null)}>
              <div className="rent-document-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="rent-document-modal-header">
                  <button
                    type="button"
                    className="rent-document-modal-icon-button"
                    onClick={() => setViewingDocumentHtml(null)}
                  >
                    <TbX size={24} />
                  </button>
                </div>
                <div className="rent-document-modal-body">
                  {viewingDocumentTitle && (
                    <h3 className="rent-document-modal-title">{viewingDocumentTitle}</h3>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: viewingDocumentHtml }} />
                </div>
                <div className="rent-document-modal-footer">
                  <button
                    type="button"
                    className="rent-document-modal-close-button"
                    onClick={() => setViewingDocumentHtml(null)}
                  >
                    Kapat
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && (
          <SuccessModal
            onClose={() => {
              setShowSuccessModal(false);
              setSuccessMessage("");
              navigate("/mainpage");
            }}
            message={successMessage}
          />
        )}
      </form>
    </div>
  );
}

export default RentConfirmation;
